// ESO — Enhetens StyrkeOppbygging (operativ). Tre deler i ett GNÅ-panel:
//   1) DISTRIBUER (befal): tildel en operativ sjekkliste (rolle × echelon) ned
//      til et mål (echelon + HV-territorium). Gates av RBAC (canDistributeChecklist).
//   2) MIN SJEKKLISTE: hak av de EKTE GNÅ-punktene og meld status/KLAR.
//   3) STYRKEOPPBYGGING: se KLAR-status rulle opp Lag → Tropp → … → Brigade.
// Deles i sanntid over hubben (collab: kind 'checklist' + 'eso'), org-scopet.
// Ren DOM (all felt-tekst via textContent). Docked + pop-out.
import {
  templateFor, distributableLevels,
  ESO_ROLE_LABEL, ECHELON_LABEL, ECHELON_ABBR, ESO_READINESS_LABEL, ESO_READINESS_COLOR,
  ChecklistAssignmentSchema, EsoSoldierStatusSchema,
  soldierReadiness, latestPerSoldier, unitReadiness, aggregateReadiness,
  assignmentLabel, addressLabel,
  type EsoRole, type Echelon, type EsoSoldierStatus, type ChecklistAssignment, type EsoReadiness,
} from '@skade/contracts';
import { canDistributeChecklist, type Role, type OrgUnit } from '@skade/rbac';

const ROLES: EsoRole[] = ['standard', 'sanitet', 'skarpskytter'];

export interface EsoSelf { id: string; role: Role; org: OrgUnit }

export interface EsoHooks {
  self: EsoSelf;
  getStatuses: () => EsoSoldierStatus[];
  getAssignments: () => ChecklistAssignment[];
  /** Kringkast egen enkeltmanns-status (rollup-input). */
  publishStatus: (s: EsoSoldierStatus) => void;
  /** Distribuer en sjekkliste-tildeling nedover kommandokjeden. */
  distribute: (a: ChecklistAssignment) => void;
}

type Tab = 'force' | 'mine' | 'distribute';

export class EsoPanel {
  readonly el: HTMLElement;
  private open = false;
  private tab: Tab = 'force';
  private bodyEl!: HTMLElement;
  private tabsEl!: HTMLElement;
  // «Min sjekkliste»-tilstand
  private myRole: EsoRole = 'standard';
  private myLevel: Echelon = 'enkeltmann';
  private checked = new Set<string>();

  constructor(private hooks: EsoHooks, embedded = false) {
    this.el = this.build();
    if (embedded) { this.open = true; this.el.classList.add('open', 'embedded'); }
    this.render();
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); if (this.open) this.render(); }

  /** Kalles når nye collab-events (eso/checklist) er mottatt. */
  onData() { if (this.open) this.render(); }

  private canDistribute(): boolean {
    // Grov UI-gate (hubben håndhever autoritativt): har rollen command i det hele tatt?
    return canDistributeChecklist({ id: this.hooks.self.id, role: this.hooks.self.role, org: this.hooks.self.org }, {
      echelon: 'enkeltmann', ...this.hooks.self.org,
    });
  }

  private build(): HTMLElement {
    const p = div('report-composer eso-panel');
    const head = div('report-head');
    head.append(span('report-title', 'ESO — Styrkeoppbygging'));
    const x = document.createElement('button'); x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    this.tabsEl = div('eso-tabs');
    p.append(this.tabsEl);
    this.bodyEl = div('eso-body');
    p.append(this.bodyEl);
    return p;
  }

  private render() {
    // Faner (distribuer vises kun for befal)
    this.tabsEl.innerHTML = '';
    const tabs: Array<{ id: Tab; label: string }> = [
      { id: 'force', label: 'Styrkeoppbygging' },
      { id: 'mine', label: 'Min sjekkliste' },
    ];
    if (this.canDistribute()) tabs.push({ id: 'distribute', label: 'Distribuer' });
    if (this.tab === 'distribute' && !this.canDistribute()) this.tab = 'force';
    for (const t of tabs) {
      const b = document.createElement('button');
      b.className = 'eso-tab' + (this.tab === t.id ? ' active' : '');
      b.textContent = t.label;
      b.onclick = () => { this.tab = t.id; this.render(); };
      this.tabsEl.append(b);
    }
    this.bodyEl.innerHTML = '';
    if (this.tab === 'force') this.renderForce();
    else if (this.tab === 'mine') this.renderMine();
    else this.renderDistribute();
  }

  // ── 3) Rollup Lag → Brigade ────────────────────────────────────────────────
  private renderForce() {
    const statuses = latestPerSoldier(this.hooks.getStatuses());
    if (!statuses.length) {
      this.bodyEl.append(note('Ingen innmeldte statuser ennå. Enkeltmenn melder fra «Min sjekkliste» eller fra GNÅ-appen.'));
      return;
    }
    // Grupper etter selvrapportert enhet (lag). Rollup: hver enhet → beredskap,
    // og en samlet aggregert beredskap på toppen (bygger nedenfra).
    const byUnit = new Map<string, EsoSoldierStatus[]>();
    for (const s of statuses) {
      const u = s.unit || '—';
      (byUnit.get(u) ?? byUnit.set(u, []).get(u)!).push(s);
    }
    const overall = aggregateReadiness([...byUnit.values()].map(unitReadiness));
    const readyTotal = statuses.filter((s) => s.ready).length;

    const banner = div('eso-rollup-banner');
    banner.append(readinessDot(overall));
    banner.append(span('eso-rollup-label', `Samlet: ${ESO_READINESS_LABEL[overall]}`));
    banner.append(span('checklist-note', `${readyTotal}/${statuses.length} enkeltmenn meldt KLAR · ${byUnit.size} enheter`));
    this.bodyEl.append(banner);

    const list = div('eso-unit-list');
    for (const [unit, soldiers] of [...byUnit.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      const r = unitReadiness(soldiers);
      const readyN = soldiers.filter((s) => s.ready).length;
      const row = div('eso-unit-row');
      row.append(readinessDot(r));
      const main = div('eso-unit-main');
      main.append(span('eso-unit-name', unit));
      main.append(span('checklist-note', `${readyN}/${soldiers.length} klar · ${ESO_READINESS_LABEL[r]}`));
      row.append(main);
      list.append(row);
      // Enkeltmenn under enheten (sammenslått, nyeste per soldat)
      for (const s of latestPerSoldier(soldiers).sort((a, b) => a.name.localeCompare(b.name))) {
        const sr = div('eso-soldier-row');
        sr.append(readinessDot(soldierReadiness(s)));
        sr.append(span('eso-soldier-name', short(s.name || s.soldierId)));
        sr.append(span('checklist-note', `${s.checked.length}/${s.total} · ${ESO_ROLE_LABEL[s.role]} · ${ECHELON_ABBR[s.level]}`));
        list.append(sr);
      }
    }
    this.bodyEl.append(list);
  }

  // ── 2) Min sjekkliste (enkeltmann) ─────────────────────────────────────────
  private renderMine() {
    const form = div('eso-mine-form');
    const roleSel = sel(ROLES.map((r) => ({ v: r, t: ESO_ROLE_LABEL[r] })));
    roleSel.value = this.myRole;
    roleSel.onchange = () => { this.myRole = roleSel.value as EsoRole; this.checked.clear(); this.render(); };
    const lvlSel = sel(distributableLevels().map((l) => ({ v: l, t: ECHELON_LABEL[l] })));
    lvlSel.value = this.myLevel;
    lvlSel.onchange = () => { this.myLevel = lvlSel.value as Echelon; this.checked.clear(); this.render(); };
    form.append(labeled('Rolle', roleSel), labeled('Nivå', lvlSel));
    this.bodyEl.append(form);

    const tmpl = templateFor(this.myRole, this.myLevel);
    const items = div('eso-items');
    for (const it of tmpl.items) {
      const row = document.createElement('label'); row.className = 'eso-check-row';
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = this.checked.has(it.id);
      cb.onchange = () => { if (cb.checked) this.checked.add(it.id); else this.checked.delete(it.id); this.updateMineCount(); };
      row.append(cb, span('eso-check-text', it.text));
      items.append(row);
    }
    this.bodyEl.append(items);

    const count = span('checklist-note', '');
    count.dataset.role = 'eso-mine-count';
    this.bodyEl.append(count);
    this.updateMineCount();

    const btns = div('eso-mine-btns');
    const sendBtn = document.createElement('button'); sendBtn.className = 'targetlist-btn'; sendBtn.textContent = 'Send status';
    sendBtn.onclick = () => this.publishMine(false);
    const readyBtn = document.createElement('button'); readyBtn.className = 'report-submit'; readyBtn.textContent = 'Meld KLAR';
    readyBtn.onclick = () => this.publishMine(true);
    btns.append(sendBtn, readyBtn);
    this.bodyEl.append(btns);
  }

  private updateMineCount() {
    const el = this.bodyEl.querySelector('[data-role="eso-mine-count"]');
    const total = templateFor(this.myRole, this.myLevel).items.length;
    if (el) el.textContent = `${this.checked.size}/${total} avhuket`;
  }

  private publishMine(ready: boolean) {
    const tmpl = templateFor(this.myRole, this.myLevel);
    const org = this.hooks.self.org;
    const unit = org.team || org.troop || org.area || org.district || '—';
    const parsed = EsoSoldierStatusSchema.safeParse({
      soldierId: this.hooks.self.id,
      name: short(this.hooks.self.id),
      unit,
      role: this.myRole,
      level: this.myLevel,
      templateId: `${this.myRole}.${this.myLevel}.v1`,
      checked: [...this.checked],
      total: tmpl.items.length,
      ready,
      tsMs: Date.now(),
    });
    if (!parsed.success) return;
    this.hooks.publishStatus(parsed.data);
    this.tab = 'force';
    this.render();
  }

  // ── 1) Distribuer (befal) ──────────────────────────────────────────────────
  private renderDistribute() {
    const self = this.hooks.self;
    const form = div('eso-dist-form');
    const roleSel = sel(ROLES.map((r) => ({ v: r, t: ESO_ROLE_LABEL[r] })));
    const lvlSel = sel(distributableLevels().map((l) => ({ v: l, t: ECHELON_LABEL[l] }))); lvlSel.value = 'lag';
    const district = inp('Distrikt (HV-xx)', self.org.district ?? '');
    const area = inp('Område', self.org.area ?? '');
    const troop = inp('Tropp', self.org.troop ?? '');
    const team = inp('Lag', self.org.team ?? '');
    const noteInp = inp('Merknad (valgfri)', '');
    form.append(labeled('Rolle', roleSel), labeled('Echelon', lvlSel), district.wrap, area.wrap, troop.wrap, team.wrap, noteInp.wrap);

    const result = div('admin-result');
    const btn = document.createElement('button'); btn.className = 'report-submit'; btn.textContent = 'Distribuer sjekkliste';
    btn.onclick = () => {
      result.innerHTML = '';
      const target = {
        echelon: lvlSel.value as Echelon,
        district: district.control.value.trim() || undefined,
        area: area.control.value.trim() || undefined,
        troop: troop.control.value.trim() || undefined,
        team: team.control.value.trim() || undefined,
      };
      // Klient-forhåndssjekk (hubben håndhever autoritativt igjen ved fanout).
      if (!canDistributeChecklist({ id: self.id, role: self.role, org: self.org }, target)) {
        result.append(span('admin-err', 'Avvist: utenfor din kommando-echelon eller org-scope.'));
        return;
      }
      const parsed = ChecklistAssignmentSchema.safeParse({
        id: `as-${Date.now().toString(36)}-${Math.floor(performance.now() % 1e6).toString(36)}`,
        role: roleSel.value as EsoRole,
        level: lvlSel.value as Echelon,
        target,
        issuedBy: self.id,
        issuedMs: Date.now(),
        note: noteInp.control.value.trim(),
      });
      if (!parsed.success) { result.append(span('admin-err', 'Ugyldig tildeling.')); return; }
      this.hooks.distribute(parsed.data);
      result.append(span('checklist-note', `Distribuert: ${assignmentLabel(parsed.data)} → ${addressLabel(parsed.data.target)}`));
      this.render();
    };
    form.append(btn);
    this.bodyEl.append(form, result);

    // Aktive tildelinger (mottatt over hubben)
    const active = this.hooks.getAssignments().sort((a, b) => b.issuedMs - a.issuedMs);
    if (active.length) {
      this.bodyEl.append(span('checklist-cat', 'Aktive tildelinger'));
      const list = div('eso-assign-list');
      for (const a of active.slice(0, 50)) {
        const row = div('eso-assign-row');
        row.append(span('eso-assign-label', assignmentLabel(a)));
        row.append(span('checklist-note', `→ ${addressLabel(a.target)} · ${short(a.issuedBy)}${a.note ? ' · ' + a.note : ''}`));
        list.append(row);
      }
      this.bodyEl.append(list);
    }
  }
}

// ── DOM-hjelpere ──────────────────────────────────────────────────────────────
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function note(text: string): HTMLElement { return span('checklist-note', text); }
function short(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }
function readinessDot(r: EsoReadiness): HTMLElement {
  const d = document.createElement('span'); d.className = 'eso-dot'; d.style.background = ESO_READINESS_COLOR[r]; d.title = ESO_READINESS_LABEL[r]; return d;
}
function sel(opts: { v: string; t: string }[]): HTMLSelectElement {
  const s = document.createElement('select'); s.className = 'checklist-sel';
  for (const o of opts) { const opt = document.createElement('option'); opt.value = o.v; opt.textContent = o.t; s.append(opt); }
  return s;
}
function inp(placeholder: string, value: string): { wrap: HTMLElement; control: HTMLInputElement } {
  const control = document.createElement('input'); control.className = 'report-input'; control.placeholder = placeholder; control.value = value;
  return { wrap: labeled(placeholder, control), control };
}
function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = div('report-field'); wrap.append(span('report-field-label', label), control); return wrap;
}
